import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DecryptObjectFunction,
  EncryptObjectFunction,
} from "../../utils/cryptoFunction";
import {
  handleDecodeJSON,
  handleEncodeJson,
} from "../../utils/encodeDecodeJson";
import examActions from "../../Redux/exam/actions";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";

import commonActions from "../../Redux/common/actions";
import Cookies from "js-cookie";
import tickImage from "../../assets/images/tick.png";
import RemainingTimeModal from "../liveTest/remainingTimeModal";
import sampleTestActions from "../../Redux/sampleTest/actions";
import { IoMdHome } from "react-icons/io";
import useFetchCandidateTokenDetails from "../../hooks/useFetchCandidateTokenDetails";
import { auditLog } from "../../utils/auditLog";
dayjs.extend(utc);
dayjs.extend(duration);

export default function QuestionNumberComponent({
  sampleQuestionAnsAnswers,
  handleAuditLog,
  setNextQuestion,
  updatedQuestionsList,
  setUpdatedQuestionsList,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({});
  const [candidateTokenDetails, setCandidateTokenDetails] = useState(null);
  const [currentQuestionStatus, setCurrentQuestionStatus] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [previousSectionIndex, setPreviousSectionIndex] = useState(-1);
  const [currentSection, setCurrentSection] = useState(null);
  const [nextSection, setNextSection] = useState(null);
  const [initialRender, setInitialRender] = useState(true);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());

  useEffect(() => {
    const storedVisited = sessionStorage.getItem("visitedQuestions");
    if (storedVisited) {
      setVisitedQuestions(new Set(JSON.parse(storedVisited)));
    }

    if (currentSrNo) {
      setVisitedQuestions((prev) => new Set([...prev, currentSrNo.toString()]));
    }
  }, [visitedQuestions]);

  const { heartBeat, projectDetails } = useSelector(
    (state) => state.examReducer
  );

  const encryptedParams = searchParams.get("qNo");

  let decryptedParams = {};
  if (encryptedParams) {
    try {
      decryptedParams = DecryptObjectFunction(encryptedParams);
    } catch (error) {
      console.error("Error decrypting parameters:", error);
      decryptedParams = {};
    }
  } else {
    console.warn("No query parameter 'qNo' found.");
  }

  const imageSize = decryptedParams?.img || "L";
  const language = decryptedParams?.lang || "english";
  const currentSrNo = decryptedParams?.SrNo || "defaultSrNo";

  useFetchCandidateTokenDetails({ setCandidateTokenDetails });

  useEffect(() => {
    dispatch({
      type: examActions.GET_PROJECT_DETAILS,
      payload: {
        data: {
          project: `${Cookies.get("test_engine_project")}`,
        },
      },
    });
  }, []);

  useEffect(() => {
    if (updatedQuestionsList) {
      const groupedData = Object.entries(updatedQuestionsList).reduce(
        (acc, [key, value]) => {
          if (!acc[value.section]) {
            acc[value.section] = [];
          }

          let status = value.status.toLowerCase();
          if (
            initialRender &&
            status === "unanswered" &&
            !visitedQuestions.has(key)
          ) {
            status = "not-viewed";
          }

          acc[value.section].push({
            question_number: key,
            status: status,
          });
          return acc;
        },
        {}
      );

      if (currentFilter !== "all") {
        Object.keys(groupedData).forEach((section) => {
          groupedData[section] = groupedData[section].filter(
            (q) => q.status === currentFilter
          );
        });
      }

      setData(groupedData);
      setNextQuestion(Object.values(groupedData).flat());

      if (initialRender) {
        setInitialRender(false);
      }
    }
  }, [
    updatedQuestionsList,
    currentFilter,
    setNextQuestion,
    visitedQuestions,
    initialRender,
  ]);

  const handleFilter = (key) => {
    setCurrentFilter((prevFilter) => {
      if (key === "not_viewed") {
        return prevFilter === "unanswered" ? "all" : "unanswered";
      }
      return prevFilter === key ? "all" : key;
    });
    const registrationNumber = candidateTokenDetails?.user?.id || "";
    const rollNumber = candidateTokenDetails?.user?.rollNo || "";
    const capitalizedFilter= key === 'not_viewed' ? 'Not Viewed' : key === 'Answered_and_marked' ? 'Answered and Marked' : key.charAt(0).toUpperCase() + key.slice(1);
    auditLog({
      data: {
          RegistrationNumber: registrationNumber,
          RollNumber: rollNumber,
          Activity: `User selected ${capitalizedFilter} Filter`,
          IPaddress: "",
        },
      dispatch,
      heartBeat,
    });
  };

  const getStatusCount = (status) => {
    return Object.values(updatedQuestionsList || {}).filter((q) => {
      const questionNumber = Object.keys(updatedQuestionsList).find(
        (key) => updatedQuestionsList[key] === q
      );

      if (status === "not-viewed") {
        return (
          !visitedQuestions.has(questionNumber) &&
          (q.status.toLowerCase() === "unanswered" || !q.status)
        );
      }

      if (status === "unanswered") {
        return (
          visitedQuestions.has(questionNumber) &&
          q.status.toLowerCase() === "unanswered"
        );
      }

      return q.status.toLowerCase() === status;
    }).length;
  };

  const handleQuestionNavigation = async (data) => {
    const registrationNumber = candidateTokenDetails?.user?.id || "";
    const rollNumber = candidateTokenDetails?.user?.rollNo || "";
    auditLog({
      data: {
          RegistrationNumber: registrationNumber,
          RollNumber: rollNumber,
          Activity: `User selected Question No ${data.question_number}`,
          IPaddress: "",
      },
      dispatch,
      heartBeat,
    });
    const newVisited = new Set([...visitedQuestions, data.question_number]);
    setVisitedQuestions(newVisited);
    sessionStorage.setItem("visitedQuestions", JSON.stringify([...newVisited]));

    let newUpdatedQuestionsList = { ...updatedQuestionsList };
    if (data?.status === "not_viewed") {
      newUpdatedQuestionsList[data.question_number] = {
        ...newUpdatedQuestionsList[data.question_number],
        status: "UNANSWERED",
      };
    }
    setUpdatedQuestionsList(newUpdatedQuestionsList);

    const formData = {
      imageSize: `${imageSize}`,
      action:
        currentQuestionStatus === "ANSWERED"
          ? "next"
          : currentQuestionStatus === "SKIPPED"
          ? "skip"
          : currentQuestionStatus === "MARKED"
          ? "mark"
          : "next",
      currentSrNo: currentSrNo,
      nextSrNo: parseInt(data?.question_number),
      selectedAnswer: sampleQuestionAnsAnswers?.questionData?.selectedAnswer
        ? sampleQuestionAnsAnswers?.questionData?.selectedAnswer
        : 0,
      language: `${language}`,
    };
    const encodedData = await handleEncodeJson({ encodeJSON: formData });
    const queryParams = {
      SrNo: parseInt(data?.question_number),
      lang: `${language}`,
      img: `${imageSize}`,
    };
    dispatch({
      type: sampleTestActions.GET_SAMPLE_SUBMIT_ANSWER,
      payload: {
        data: encodedData,
        navigate: navigate,
        queryParams: queryParams,
      },
    });
  };

  //   const newVisited = new Set([...visitedQuestions, data.question_number]);
  //   setVisitedQuestions(newVisited);
  //   sessionStorage.setItem("visitedQuestions", JSON.stringify([...newVisited]));
  //   const formData = {
  //     imageSize: `L`,
  //     action:
  //       currentQuestionStatus === "ANSWERED"
  //         ? "next"
  //         : currentQuestionStatus === "SKIPPED"
  //         ? "skip"
  //         : currentQuestionStatus === "MARKED"
  //         ? "mark"
  //         : "next",
  //     currentSrNo: currentSrNo,
  //     nextSrNo: parseInt(data?.question_number),
  //     selectedAnswer:
  //       sampleQuestionAnsAnswers?.questionData?.selectedAnswer || 0,
  //     language: `english`,
  //   };

  //   const encodedData = await handleEncodeJson({ encodeJSON: formData });
  //   const queryParams = {
  //     SrNo: parseInt(data?.question_number),
  //     lang: `L`,
  //     img: `english`,
  //   };
  //   const encryptedParams = EncryptObjectFunction(queryParams);
  //   dispatch({
  //     type: sampleTestActions.GET_SAMPLE_SUBMIT_ANSWER,
  //     payload: {
  //       data: encodedData,
  //       navigate: (path) => navigate(`${path}?qNo=${encryptedParams}`),
  //     },
  //   });
  // };

  useEffect(() => {
    if (updatedQuestionsList && currentSrNo) {
      const questionData = updatedQuestionsList[currentSrNo];
      setCurrentQuestionStatus(questionData ? questionData.status : null);
    }
  }, [currentSrNo, sampleQuestionAnsAnswers]);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    const totalSectionDurations =
      candidateTokenDetails?.configuration?.sections.map((section) =>
        dayjs
          .duration({
            hours: Number(section.sectionTime.split(":")[0]),
            minutes: Number(section.sectionTime.split(":")[1]),
          })
          .asMilliseconds()
      );

    const calculateTime = () => {
      const now = dayjs();
      const examStart = dayjs
        .utc(candidateTokenDetails?.configuration?.startTime)
        .subtract(5, "hours")
        .subtract(30, "minutes");

      if (now.isBefore(examStart)) {
        setCurrentSectionIndex(-1);
        setRemainingTime(examStart.diff(now));
        return;
      }

      const elapsed = now.diff(examStart);
      let accumulatedTime = 0;

      for (
        let i = 0;
        i < candidateTokenDetails?.configuration?.sections?.length;
        i++
      ) {
        const sectionDuration = totalSectionDurations[i];

        if (elapsed < accumulatedTime + sectionDuration) {
          if (currentSectionIndex !== i) {
            setPreviousSectionIndex(currentSectionIndex);
            setCurrentSectionIndex(i);

            if (currentSectionIndex >= 0) {
              setCurrentSection(
                candidateTokenDetails?.configuration?.sections[
                  currentSectionIndex
                ].sectionTitle
              );
              setNextSection(
                candidateTokenDetails?.configuration?.sections[i].sectionTitle
              );
              dispatch({
                type: commonActions.SET_SHOW_MODAL,
                payload: { show: true, type: null, method: "A", data: null },
              });
            }
          }
          setRemainingTime(accumulatedTime + sectionDuration - elapsed);
          return;
        }
        accumulatedTime += sectionDuration;
      }
      setCurrentSectionIndex(
        candidateTokenDetails?.configuration?.sections?.length
      );
      setRemainingTime(0);
    };

    const timer = setInterval(calculateTime, 1000);
    calculateTime();
    return () => clearInterval(timer);
  }, [
    candidateTokenDetails?.configuration?.sections,
    candidateTokenDetails?.configuration?.startTime,
    heartBeat,
  ]);

  const formatTime = (ms) => {
    const duration = dayjs.duration(ms);
    const hours = duration.hours().toString().padStart(2, "0");
    const minutes = duration.minutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const statusConfigs = [
    {
      key: "answered",
      label: "Answered",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(84, 179, 55, 1)",
        clipPath:
          "polygon(25% 0%, 75% 0%, 100% 50%, 100% 100%, 0 100%, 0% 50%)",
      },
    },
    {
      key: "unanswered",
      label: "Unanswered",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(234, 104, 58, 1)",
        clipPath: "polygon(0 0, 100% 0, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
      },
    },
    {
      key: "not_viewed",
      label: "Not Viewed",
      style: {
        width: "22.82px",
        height: "22.82px",
        background:
          "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
      },
    },
    {
      key: "marked",
      label: "Marked for Review",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(130, 91, 172, 1)",
        borderRadius: "50%",
      },
    },
    {
      key: "answered_and_marked",
      label: "Answered & Marked for Review",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(130, 91, 172, 1)",
        borderRadius: "50%",
        position: "relative",
      },
    },
  ];

  const getQuestionStyle = (
    questionStatus,
    isCurrentQuestion,
    questionNumber,
    visitedQuestions
  ) => {
    if (isCurrentQuestion) {
      return {
        background: "#808080",
      };
    }

    if (
      !visitedQuestions.has(questionNumber.toString()) &&
      (questionStatus?.toLowerCase() === "unanswered" || !questionStatus)
    ) {
      return {
        background:
          "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
      };
    }

    switch (questionStatus?.toLowerCase()) {
      case "answered":
        return {
          background: "rgba(84, 179, 55, 1)",
          clipPath:
            "polygon(25% 0%, 75% 0%, 100% 50%, 100% 100%, 0 100%, 0% 50%)",
        };
      case "unanswered":
        return {
          background: "rgba(234, 104, 58, 1)",
          clipPath:
            "polygon(0 0, 100% 0, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
        };
      case "marked":
        return {
          background: "rgba(130, 91, 172, 1)",
          borderRadius: "50%",
        };
      case "answered_and_marked":
        return {
          background: "rgba(130, 91, 172, 1)",
          borderRadius: "50%",
        };
      default:
        return {
          background:
            "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
        };
    }
  };

  const isSection = Object.values(updatedQuestionsList || {}).every(
    (res) => "section" in res
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid
          sx={{
            backgroundColor: "rgba(240, 240, 251, 1)",
            height: "32px",
            borderRadius: "8px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
          p={1}
        >
          <IoMdHome
            size={20}
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate(`/${Cookies.get("test_engine_project")}/home`);
              sessionStorage.setItem("visitedQuestions", JSON.stringify(""));
              sessionStorage.removeItem("updatedQuestionsList");
              auditLog({
                data: {
                  RegistrationNumber: `${candidateTokenDetails?.user?.id}`,
                  RollNumber: `${candidateTokenDetails?.user?.rollNo}`,
                  Activity: "User selected Sample Test Home",
                  IPaddress: "",
                },
                dispatch,
                heartBeat,
              });
            }}
            title="Home"
          />
          <Typography
            sx={{
              color: "rgba(63, 65, 209, 1)",
              fontSize: "14px",
              fontWeight: "bold",
              lineHeight: 0,
            }}
          >
            Summary
          </Typography>
        </Grid>
        <Grid
          container
          sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}
          p={2}
        >
          {statusConfigs.map((config) => (
            <Box
              key={config.key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() => {
                handleFilter(config.key);
                // handleAuditLog("", "", "", config.key);
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>{config.label}</Typography>
              <Box
                sx={{
                  ...config.style,
                  color: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                }}
              >
                {getStatusCount(config.key)}
                {config.key === "answered_and_marked" && (
                  <Box
                    component="img"
                    alt="check"
                    src={tickImage}
                    sx={{
                      position: "absolute",
                      bottom: -1,
                      right: -4,
                      height: "13px",
                      width: "auto",
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>
      {!isSection && (
        <Grid item xs={12}>
          <Divider />
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sx={{
          height: isSection && "320px",
          overflowY: isSection && "scroll",
          paddingRight: "8px",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#e0e0e0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "blue",
            borderRadius: "10px",
            border: "2px solid #e0e0e0",
            height: "10px",
            paddingBottom: "12px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "darkblue",
          },
        }}
      >
        {Object.entries(data).map(([section, questions], index) => {
          return (
            <Box
              key={section}
              sx={{
                mb: 2,
              }}
            >
              {isSection && (
                <Box
                  p={1}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    backgroundColor: "rgba(240, 240, 251, 1)",
                    height: "32px",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(63, 65, 209, 1)",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {section}
                  </Typography>
                  {/* <Typography variant="body2">
                    {index < currentSectionIndex && (
                      <p>Time's up for this section!</p>
                    )}
                    {index === currentSectionIndex && (
                      <p>Time Remaining : {formatTime(remainingTime)}</p>
                    )}
                    {index > currentSectionIndex && (
                      <p>This section is locked</p>
                    )}
                  </Typography> */}
                </Box>
              )}
              <Grid
                sx={{
                  height: !isSection && "290px",
                  overflowY: !isSection && "scroll",
                  paddingRight: "8px",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#e0e0e0",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "blue",
                    borderRadius: "10px",
                    border: "2px solid #e0e0e0",
                    height: "10px",
                    paddingBottom: "12px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "darkblue",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 2,
                  }}
                >
                  {questions?.map((obj) => {
                    const isCurrentQuestion =
                      currentSrNo === parseInt(obj.question_number);
                    let displayStatus = obj.status;
                    if (
                      !visitedQuestions.has(obj.question_number) &&
                      obj.status === "unanswered"
                    ) {
                      displayStatus = "not-viewed";
                    }

                    const baseStyles = getQuestionStyle(
                      obj.status,
                      isCurrentQuestion,
                      obj.question_number,
                      visitedQuestions
                    );
                    return (
                      <Box
                        key={obj.question_number}
                        onClick={() => {
                          if (!isSection) {
                            handleQuestionNavigation(obj);
                            // handleAuditLog("", "", obj.question_number);
                          } else if (index === currentSectionIndex) {
                            handleQuestionNavigation(obj);
                            // handleAuditLog("", "", obj.question_number);
                          }
                        }}
                        sx={{
                          ...baseStyles,
                          width: "25px",
                          height: "25px",
                          top: "4.59px",
                          left: "4.59px",
                          gap: "0px",
                          color: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          cursor: isSection
                            ? index === currentSectionIndex
                              ? "pointer"
                              : "not-allowed"
                            : "pointer",
                          opacity: isSection
                            ? index === currentSectionIndex
                              ? 1
                              : 0.5
                            : 1,
                          position: "relative",
                          "&:hover": {
                            transform: isCurrentQuestion
                              ? "none"
                              : "scale(1.05)",
                            transition: "transform 0.2s ease-in-out",
                          },
                        }}
                      >
                        {obj.question_number}
                        {obj.status === "answered_and_marked" &&
                          !isCurrentQuestion && (
                            <Box
                              component="img"
                              alt="check"
                              src={tickImage}
                              sx={{
                                position: "absolute",
                                bottom: -1,
                                right: -4,
                                height: "13px",
                                width: "auto",
                              }}
                            />
                          )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Box>
          );
        })}
      </Grid>
      {candidateTokenDetails?.configuration?.sections?.length > 0 && (
        <RemainingTimeModal
          currentSection={
            candidateTokenDetails?.configuration?.sections[
              currentSectionIndex - 1
            ]?.sectionTitle
          }
          nextSection={
            candidateTokenDetails?.configuration?.sections[currentSectionIndex]
              ?.sectionTitle
          }
        />
      )}
    </Grid>
  );
}
